import React from "react";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";

function isChunkLoadError(error: any): boolean {
  return (
    error?.name === "ChunkLoadError" ||
    error?.message?.includes("Loading chunk") ||
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    error?.message?.includes("timeout")
  );
}

interface ErrorFallbackUIProps {
  title: string;
  description: string;
  showRetry?: boolean;
  showHome?: boolean;
  error?: any;
}

const ErrorFallbackUI: React.FC<ErrorFallbackUIProps> = ({
  title,
  description,
  showRetry = true,
  showHome = true,
  error,
}) => {
  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = window.location.origin + window.location.pathname;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-background flex items-center justify-center px-4 transition-colors">
      <div className="max-w-md w-full bg-white dark:bg-dark-foreground rounded-2xl shadow-lg p-8 text-center transition-colors">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
          <svg
            className="w-8 h-8 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h1>

        {/* Description */}
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
          {description}
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showRetry && (
            <button
              onClick={handleRetry}
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try Again
            </button>
          )}
          {showHome && (
            <button
              onClick={handleGoHome}
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
            >
              Go to Home
            </button>
          )}
        </div>

        {/* Error details for dev (collapsed) */}
        {process.env.NODE_ENV === "development" && error && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300">
              Technical details
            </summary>
            <pre className="mt-2 text-xs text-red-400 bg-gray-900 dark:bg-gray-800 rounded-lg p-3 overflow-auto max-h-40">
              {error?.message || String(error)}
              {error?.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

/**
 * Fallback component for react-error-boundary (used at App root level)
 */
function Fallback({ error }: { error: any }) {
  if (isChunkLoadError(error)) {
    return (
      <ErrorFallbackUI
        title="Connection Issue"
        description="We're having trouble loading this page. This usually happens due to a slow or unstable network connection. Please check your internet and try again."
        error={error}
      />
    );
  }

  return (
    <ErrorFallbackUI
      title="Something Went Wrong"
      description="An unexpected error occurred. Please try refreshing the page. If the problem persists, contact our support team."
      error={error}
    />
  );
}

/**
 * Route error fallback for React Router's errorElement
 */
export function RouteErrorFallback() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <ErrorFallbackUI
          title="Page Not Found"
          description="The page you're looking for doesn't exist or has been moved."
          showRetry={false}
        />
      );
    }

    return (
      <ErrorFallbackUI
        title="Something Went Wrong"
        description={error.statusText || "An unexpected error occurred."}
        error={error}
      />
    );
  }

  const err = error as Error;

  if (isChunkLoadError(err)) {
    return (
      <ErrorFallbackUI
        title="Connection Issue"
        description="We're having trouble loading this page. This usually happens due to a slow or unstable network connection. Please check your internet and try again."
        error={err}
      />
    );
  }

  return (
    <ErrorFallbackUI
      title="Something Went Wrong"
      description="An unexpected error occurred. Please try refreshing the page. If the problem persists, contact our support team."
      error={err}
    />
  );
}

export default Fallback;
